import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import { Skeleton } from 'antd';

import { useStores } from '@/hooks/use-stores';
import EPTierLockCover from '@/comp/common/EPTierLockCover';
import { useValueDashboard } from '@/hooks/useValueDashboard';

import HighChartsContainer from './HighChartsContainer';
import Card from './../Card';
import Legend from './Legend';
import YearPager from './../YearPager';
import YearlyViewButton from './../YearlyViewButton';
import { EnergySavingContainer } from './styled';
import { BaseEnergySavingsProps } from './EnergySaving.interface';

highchartsMore(Highcharts);
solidGauge(Highcharts);

const BaseEnergySavings: React.FC<BaseEnergySavingsProps> = observer(
  ({
    description,
    isLocked = false,
    title,
    highchartOptions,
    shouldRestrictEnergySavings = false,
    withTooltip = false,
    tooltipProps,
    children,
    ...rest
  }) => {
    const { t } = useTranslation();
    const { stationStore, energySavingsStore, energySavingsGraphStore } = useStores();
    const { baselineStart, baselineEnd } = useValueDashboard();

    const baselinePeriodData = energySavingsGraphStore.baselineData
      ? {
          baselineStart,
          baselineEnd,
        }
      : undefined;

    const isLoading = !energySavingsStore.isReady || stationStore.isLoading;

    return (
      <EnergySavingContainer {...rest}>
        {isLocked && (
          <EPTierLockCover
            label={t('lockedFunctionality')}
            shouldShowTooltip={false}
            message={t('backgroundDataSystemHealth')}
            disabledClick={true}
          />
        )}
        <h1>{title}</h1>
        {description && <div className="description">{description}</div>}

        <Card className="energy-savings-container">
          <YearPager
            energySavingsStore={energySavingsStore}
            yearOffsetLimit={energySavingsGraphStore.lastPage}
            baselinePeriodData={baselinePeriodData}
            isWidget={true}
          />
          {isLoading ? (
            <div className="skeleton-container">
              <Skeleton.Node active style={{ width: 200, height: 200 }} rootClassName="energy-savings-skeleton" />
              <div className="legend-container">
                <Skeleton
                  active
                  paragraph={false}
                  title={{ width: 200 }}
                  rootClassName="fit-content"
                  loading={isLoading}
                />
                <Skeleton
                  active
                  paragraph={false}
                  title={{ width: 200 }}
                  rootClassName="fit-content"
                  loading={isLoading}
                />
                <Skeleton
                  active
                  paragraph={false}
                  title={{ width: 200 }}
                  rootClassName="fit-content"
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <HighChartsContainer
              options={highchartOptions}
              isReady={energySavingsStore.isReady}
              withTooltip={withTooltip}
              tooltipProps={tooltipProps}
            >
              {energySavingsStore.isReady && highchartOptions && (
                <div className="legend-container">
                  <Legend shouldRestrictEnergySavings={shouldRestrictEnergySavings} />
                </div>
              )}
            </HighChartsContainer>
          )}
          {children}
          <YearlyViewButton
            isOpen={energySavingsGraphStore.yearlyViewOpen}
            setIsOpen={energySavingsGraphStore.setYearlyViewOpen}
          />
        </Card>
      </EnergySavingContainer>
    );
  },
);

export default BaseEnergySavings;
